import type { EraDef } from '../types';

/**
 * ERA 8 — INDUSTRIAL AGE
 * A steampunk factory floor under aerial bombardment. Everything here runs on a
 * timetable: airships loiter low, sound a klaxon, climb out of reach and carpet a
 * committed lane with fused bombs; riveting presses stamp fresh automatons onto the
 * floor on a beat; bolted vent banks hiss scalding steam in staggered rings. Soot
 * and gaslight over a riveted grid. Boss: The Brass Automaton, which runs a boiler
 * duty cycle — it locks its treads, holds fire, and blows concentric rings of steam
 * outward from its own hull (a rooted, fully targetable body you still can't stand
 * still to shoot), then purges and sprints. Palette: soot brown/brass with furnace
 * amber; enemy bullets amber (0xffb020) against arc-light cyan player fire; hazards
 * hot slag orange (0xff6a20) — delivered as DETONATIONS ON A FUSE and vent blooms
 * rather than painted lines (era 7's trick) or lobbed scatter (eras 5-6).
 * Signature behaviors owned here: `flyover`, `assemble`. Boss special: `overheat`.
 */
const ENEMY_BULLET = 0xffb020;
const HAZARD = 0xff6a20;

export const ERA_INDUSTRIAL: EraDef = {
  index: 7,
  id: 'industrial',
  name: 'Industrial Age',
  subtitle: '1890 AD',
  implemented: true,
  palette: {
    sky: 0x1b1811,
    fog: 0x322a1c,
    fogDensity: 0.026,
    ground: 0x4a3a24,
    groundAlt: 0x5a4830,
    ambient: 0x8a6a3a,
    sun: 0xffcf7a,
    sunIntensity: 1.05,
    accent: 0x7ce0ff,
    enemyBullet: ENEMY_BULLET,
    hazard: HAZARD,
  },
  environment: {
    kind: 'factory',
    arenaRadius: 70,
    groundStyle: 'grid',
    props: [
      { visual: { geo: 'box', color: 0x5a4830, emissive: 0x1a1208, emissiveIntensity: 0.18, scaleY: 3.6, anim: 'none' }, count: 22, scale: [0.6, 2.0], y: 0 },
      { visual: { geo: 'torus', color: 0xd89a3a, emissive: 0xd89a3a, emissiveIntensity: 0.7, anim: 'spin' }, count: 12, scale: [0.4, 1.1], y: 0.8 },
      { visual: { geo: 'cone', color: 0xff8020, emissive: 0xff6a20, emissiveIntensity: 1.0, anim: 'pulse' }, count: 10, scale: [0.3, 0.6], y: 0.4 },
    ],
  },
  enemies: [
    {
      id: 'n8_scuttler',
      name: 'Clockwork Scuttler',
      archetype: 'swarmer',
      hp: 34,
      speed: 4.6,
      damage: 11,
      radius: 0.40,
      xp: 9,
      visual: { geo: 'octa', color: 0xb8862a, emissive: 0x4a3410, emissiveIntensity: 0.5, scale: 0.85, anim: 'spin' },
      behavior: { type: 'chase' },
      // Filler: wind-up crabs that run straight at you and never stop arriving, because the presses keep
      // making them. Counterplay: cheap XP, but don't farm the line — kill the press that stamps them.
    },
    {
      id: 'n8_flak',
      name: 'Flak Gunner',
      archetype: 'shooter',
      hp: 56,
      speed: 3.4,
      damage: 12,
      radius: 0.50,
      xp: 15,
      visual: { geo: 'box', color: 0x8a6a3a, emissive: 0x3a2a12, emissiveIntensity: 0.4, scale: 1, scaleY: 1.1, anim: 'none' },
      behavior: { type: 'keepDistance', distance: 12, strafe: 0.3 },
      patterns: ['n8_flak_volley'],
      patternCooldown: 2.3,
      fireRange: 17,
      // Filler kiter: three paired bursts of near-instant flak needles, then a long reload.
      // Counterplay: break line the moment the first pair leaves, then punish the reload.
    },
    {
      id: 'n8_ventbank',
      name: 'Vent Bank',
      archetype: 'shooter',
      hp: 72,
      speed: 0,
      damage: 12,
      radius: 0.62,
      xp: 16,
      shardChance: 0.12,
      visual: { geo: 'capsule', color: 0x6a5a3a, emissive: 0xff6a20, emissiveIntensity: 0.8, scale: 1.0, scaleY: 1.3, anim: 'pulse' },
      behavior: { type: 'stationary' },
      patterns: ['n8_steam_burst'],
      patternCooldown: 2.0,
      fireRange: 13,
      // Bolted to the floor plates: two staggered rings of steam creep outward, stall, and set as scalding
      // patches, so its neighbourhood becomes a shrinking lattice of safe tiles. Counterplay: it can never
      // chase you — either give the plate up entirely, or kill it from outside 13 u before the lattice closes.
    },
    {
      id: 'n8_press',
      name: 'Rivet Press',
      archetype: 'tank',
      hp: 135,
      speed: 2.8,
      damage: 14,
      radius: 0.85,
      xp: 26,
      shardChance: 0.14,
      visual: { geo: 'box', color: 0x6a5236, emissive: 0xffb020, emissiveIntensity: 0.35, scale: 1.35, scaleY: 1.15, anim: 'none' },
      behavior: { type: 'assemble', enemy: 'n8_scuttler', count: 3, interval: 6.0, windup: 1.0, spread: 2.4, range: 26, driftSpeedMult: 0.45 },
      // Signature: no patterns at all — it creeps at 1.26 u/s, roots, glows for 1 s, and stamps three fresh
      // Scuttlers in a ring around itself, forever. Counterplay: the 1 s stamp is a free burst window and it
      // is the slowest thing in the era; ignore the parts and kill the machine, or the floor never empties.
    },
    {
      id: 'n8_airship',
      name: 'Bombard Airship',
      archetype: 'tank',
      hp: 120,
      speed: 3.2,
      damage: 20,
      radius: 0.90,
      xp: 30,
      shardChance: 0.16,
      visual: { geo: 'blob', color: 0x9a8a6a, emissive: 0xffb020, emissiveIntensity: 0.45, scale: 1.45, scaleY: 0.75, anim: 'bob' },
      behavior: { type: 'flyover', standoff: 10, loiterTime: 3.0, windup: 0.9, climbTime: 0.4, height: 3.4, runTime: 2.6, runSpeedMult: 2.0, bombInterval: 0.5, bombPattern: 'n8_bomb_drop' },
      patterns: ['n8_flak_spray'],
      patternCooldown: 3.0,
      fireRange: 14,
      // Signature: loiters at 10 u spraying its gondola gun (killable window), then klaxons for 0.9 s — the
      // lane locks the instant the horn stops — climbs out of reach and crosses ~17 u dead straight, laying
      // 5-6 fused bombs about 3 u apart that each airburst and leave slag. Counterplay: step OFF the lane
      // during the horn (never run down it), then dump everything into it while it loiters; it cannot be hit
      // at all while airborne and its bombs re-draw the floor behind it.
    },
    {
      id: 'n8_hauler',
      name: 'Slag Hauler',
      archetype: 'tank',
      hp: 165,
      speed: 2.5,
      damage: 22,
      radius: 0.95,
      xp: 28,
      shardChance: 0.15,
      visual: { geo: 'box', color: 0x4a3a26, emissive: 0xff6a20, emissiveIntensity: 0.4, scale: 1.5, scaleY: 0.85, anim: 'none' },
      behavior: { type: 'charge', windup: 0.8, speedMult: 2.4, duration: 1.0, cooldown: 2.8 },
      trail: { radius: 1.0, duration: 3.5, dps: 8, interval: 0.28, color: HAZARD },
      onDeath: { pattern: 'n8_slag_splash' },
      // Filler charger with teeth: a leaking crucible cart that paints molten slag everywhere it drives and
      // bursts its boiler when it dies. Counterplay: read the 0.8 s windup like any charger, but fight it in
      // open floor — its old lanes stay lethal — and never finish it standing next to it.
    },
    {
      id: 'n8_overseer',
      name: 'Brass Overseer',
      archetype: 'elite',
      elite: true,
      hp: 95,
      speed: 2.8,
      damage: 16,
      radius: 0.70,
      xp: 34,
      shardChance: 0.4,
      visual: { geo: 'spike', color: 0xd8b060, emissive: 0xffb020, emissiveIntensity: 0.75, scale: 1.35, anim: 'none' },
      behavior: { type: 'assemble', enemy: 'n8_scuttler', count: 4, interval: 4.5, windup: 0.7, spread: 2.8, range: 30, driftSpeedMult: 0.8 },
      patterns: ['n8_cog_spiral'],
      patternCooldown: 2.2,
      fireRange: 16,
      // Elite-tier press: stamps four parts every 4.5 s on a shorter, snappier windup AND sweeps rotating
      // arcs of curving cog-shards while it does it. Counterplay: the stamp windup is still your burst
      // window, but you have to walk the cog spiral's rotation to reach it — and if you stall, it out-produces you.
    },
  ],
  patterns: [
    {
      id: 'n8_flak_volley',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.2, bursts: 3, count: 2, spread: 0.22,
          bullet: { speed: 12.5, lifetime: 1.8, radius: 0.2, damage: 13, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'n8_steam_burst',
      emitters: [
        { shape: 'radial', start: 0, interval: 1.1, bursts: 2, count: 8,
          bullet: { speed: 2.8, accel: -3.0, minSpeed: 0.15, lifetime: 1.2, radius: 0.34, damage: 12, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.45, duration: 3.2, dps: 12, color: HAZARD } } },
        { shape: 'radial', start: 0.55, interval: 1.1, bursts: 2, count: 8, angle: 0.3927,
          bullet: { speed: 3.6, accel: -3.0, minSpeed: 0.15, lifetime: 1.4, radius: 0.34, damage: 12, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.45, duration: 3.2, dps: 12, color: HAZARD } } },
      ],
    },
    {
      id: 'n8_bomb_drop',
      emitters: [
        { shape: 'aimed', interval: 1, bursts: 1, count: 1,
          bullet: { speed: 0.6, accel: -0.8, minSpeed: 0.1, lifetime: 1.15, radius: 0.42, damage: 24, visual: 'blob', color: HAZARD,
                    onExpire: 'n8_bomb_burst',
                    linger: { radius: 1.75, duration: 3.5, dps: 12, color: HAZARD } } },
      ],
    },
    {
      id: 'n8_bomb_burst',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 10,
          bullet: { speed: 7.5, lifetime: 0.8, radius: 0.22, damage: 12, visual: 'shard', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'n8_flak_spray',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.45, bursts: 2, count: 5, spread: 0.9,
          bullet: { speed: 9, lifetime: 1.4, radius: 0.24, damage: 14, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'n8_cog_spiral',
      emitters: [
        { shape: 'spiral', start: 0, interval: 0.26, bursts: 6, count: 6, rotatePerBurst: 0.42,
          bullet: { speed: 6.4, curve: 0.45, lifetime: 2.2, radius: 0.26, damage: 13, visual: 'star', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'n8_slag_splash',
      emitters: [
        { shape: 'random', interval: 1, bursts: 1, count: 6, spread: 6.283,
          bullet: { speed: 3.4, accel: -1.6, minSpeed: 0.5, lifetime: 1.3, radius: 0.36, damage: 16, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.35, duration: 3.0, dps: 11, color: HAZARD } } },
      ],
    },
    {
      id: 'n8_boss_enter',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 16,
          bullet: { speed: 6.5, lifetime: 1.9, radius: 0.34, damage: 17, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'n8_boss_cog_sweep',
      emitters: [
        { shape: 'spiral', start: 0, interval: 0.22, bursts: 8, count: 7, rotatePerBurst: 0.34,
          bullet: { speed: 6.8, curve: 0.35, lifetime: 2.4, radius: 0.28, damage: 15, visual: 'star', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'n8_boss_piston_jab',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.5, bursts: 4, count: 3, spread: 0.45,
          bullet: { speed: 9, wave: { amp: 2.6, freq: 1.6 }, lifetime: 1.7, radius: 0.28, damage: 15, visual: 'needle', color: ENEMY_BULLET } },
        { shape: 'radial', start: 0.25, interval: 1.0, bursts: 2, count: 9, angle: 0.35,
          bullet: { speed: 5.4, curve: 0.3, lifetime: 2.2, radius: 0.26, damage: 13, visual: 'star', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'n8_boss_bomb_rack',
      emitters: [
        { shape: 'random', start: 0, interval: 0.55, bursts: 4, count: 2, spread: 6.283,
          bullet: { speed: 1.4, accel: -1.2, minSpeed: 0.15, lifetime: 1.4, radius: 0.44, damage: 22, visual: 'blob', color: HAZARD,
                    onExpire: 'n8_bomb_burst',
                    linger: { radius: 1.6, duration: 3.5, dps: 12, color: HAZARD } } },
      ],
    },
    {
      id: 'n8_boss_overpressure',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 20,
          bullet: { speed: 7.2, lifetime: 2.0, radius: 0.30, damage: 17, visual: 'shard', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, bursts: 1, count: 12, angle: 0.26,
          bullet: { speed: 4.4, lifetime: 2.6, radius: 0.36, damage: 15, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'n8_boss_phase3_enter',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 24,
          bullet: { speed: 7.4, curve: 0.25, lifetime: 2.1, radius: 0.30, damage: 18, visual: 'star', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, bursts: 1, count: 14, angle: 0.22,
          bullet: { speed: 4.2, lifetime: 2.8, radius: 0.40, damage: 16, visual: 'ring', color: ENEMY_BULLET } },
        { shape: 'random', interval: 1, bursts: 1, count: 4, spread: 6.283,
          bullet: { speed: 1.2, accel: -1.2, minSpeed: 0.15, lifetime: 1.3, radius: 0.44, damage: 20, visual: 'blob', color: HAZARD,
                    onExpire: 'n8_bomb_burst',
                    linger: { radius: 1.6, duration: 3.5, dps: 12, color: HAZARD } } },
      ],
    },
  ],
  boss: {
    id: 'n8_automaton',
    name: 'The Brass Automaton',
    title: 'Prime Mover',
    // Balance: 3650 base HP scales to ~38,500 effective at the era-8 boss stage multiplier (10.54x). Unlike era 7's Dragon this boss is targetable 100% of the fight — the cost is paid in fire time, not hit time: it is rooted ~28% of each cycle while steam rings sweep outward and the stationary-fire rule costs net DPS during that window (est. 15-20%, comparable to the Dragon's 18-27% untargetable). Expect ~62-76s at the expected level; tune interval/ventTime/ringInterval before HP.
    hp: 3650,
    radius: 2.7,
    speed: 1.9,
    damage: 38,
    xp: 600,
    visual: { geo: 'box', color: 0x9a7a3a, emissive: 0xffb020, emissiveIntensity: 0.7, scale: 1.5, scaleY: 1.2, anim: 'pulse' },
    phases: [
      {
        hpBelow: 1,
        name: 'Idle Governor',
        behavior: { type: 'keepDistance', distance: 11, strafe: 0.18 },
        patterns: ['n8_boss_cog_sweep', 'n8_boss_piston_jab'],
        patternCooldown: 2.3,
        transition: 1.5,
        onEnter: 'n8_boss_enter',
        special: { type: 'overheat', interval: 12, windup: 1.0, ventTime: 3.2, ringInterval: 0.85,
                   ringCount: 10, ringRadius: 1.5, ringStep: 3.4, ringTelegraph: 0.6,
                   ringDps: 11, ringDuration: 1.8, purgeTime: 0, purgeSpeedMult: 1 },
      },
      {
        hpBelow: 0.66,
        name: 'Overpressure',
        behavior: { type: 'chase' },
        speedMult: 1.1,
        patterns: ['n8_boss_cog_sweep', 'n8_boss_bomb_rack', 'n8_boss_piston_jab'],
        patternCooldown: 1.9,
        transition: 2,
        onEnter: 'n8_boss_overpressure',
        summons: { enemy: 'n8_scuttler', count: 4, interval: 9 },
        special: { type: 'overheat', interval: 10, windup: 0.85, ventTime: 4.0, ringInterval: 0.75,
                   ringCount: 11, ringRadius: 1.6, ringStep: 3.2, ringTelegraph: 0.5,
                   ringDps: 12, ringDuration: 2.0, purgeTime: 3.0, purgeSpeedMult: 1.7 },
      },
      {
        hpBelow: 0.32,
        name: 'Prime Mover',
        behavior: { type: 'orbit', radius: 7, angular: 0.85 },
        speedMult: 1.2,
        patterns: ['n8_boss_cog_sweep', 'n8_boss_bomb_rack', 'n8_boss_piston_jab'],
        patternCooldown: 1.5,
        transition: 2,
        onEnter: 'n8_boss_phase3_enter',
        summons: { enemy: 'n8_scuttler', count: 5, interval: 7 },
        special: { type: 'overheat', interval: 8, windup: 0.7, ventTime: 4.6, ringInterval: 0.6,
                   ringCount: 12, ringRadius: 1.7, ringStep: 3.0, ringTelegraph: 0.45,
                   ringDps: 13, ringDuration: 2.2, purgeTime: 3.6, purgeSpeedMult: 1.9 },
      },
    ],
  },
  stages: [
    {
      name: 'The Assembly Floor',
      duration: 220,
      objective: { type: 'waves', label: 'Clear the assembly line' },
      density: { start: 1.2, end: 3.0 },
      pool: [
        { enemy: 'n8_scuttler', weight: 10 },
        { enemy: 'n8_flak', weight: 4, minTime: 20 },
        { enemy: 'n8_ventbank', weight: 3, minTime: 40 },
        { enemy: 'n8_press', weight: 2, minTime: 55 },
        { enemy: 'n8_hauler', weight: 2, minTime: 85 },
        { enemy: 'n8_airship', weight: 1, minTime: 110 },
      ],
      waves: [
        { t: 5, enemy: 'n8_scuttler', count: 6, formation: 'ring' },
        { t: 22, enemy: 'n8_flak', count: 3, formation: 'line' },
        { t: 45, enemy: 'n8_scuttler', count: 18, formation: 'cluster' },
        { t: 65, enemy: 'n8_ventbank', count: 3, formation: 'random' },
        { t: 88, enemy: 'n8_press', count: 2, formation: 'line' },
        { t: 112, enemy: 'n8_airship', count: 1, formation: 'edge' },
        { t: 140, enemy: 'n8_flak', count: 5, formation: 'random' },
        { t: 165, enemy: 'n8_hauler', count: 2, formation: 'edge' },
        { t: 196, enemy: 'n8_overseer', count: 1, formation: 'edge' },
      ],
    },
    {
      name: 'Bombardment',
      duration: 250,
      objective: { type: 'escort', distance: 62, speed: 0.34, health: 140, radius: 7, label: 'Escort the power core' },
      density: { start: 1.6, end: 3.6 },
      pool: [
        { enemy: 'n8_scuttler', weight: 8 },
        { enemy: 'n8_flak', weight: 5 },
        { enemy: 'n8_ventbank', weight: 4 },
        { enemy: 'n8_press', weight: 4 },
        { enemy: 'n8_hauler', weight: 3, minTime: 30 },
        { enemy: 'n8_airship', weight: 2, minTime: 50 },
        { enemy: 'n8_overseer', weight: 1, minTime: 90 },
      ],
      waves: [
        { t: 5, enemy: 'n8_flak', count: 5, formation: 'ring' },
        { t: 32, enemy: 'n8_airship', count: 2, formation: 'edge' },
        { t: 60, enemy: 'n8_ventbank', count: 4, formation: 'ring' },
        { t: 90, enemy: 'n8_press', count: 3, formation: 'cluster' },
        { t: 118, enemy: 'n8_overseer', count: 1, formation: 'edge' },
        { t: 145, enemy: 'n8_scuttler', count: 30, formation: 'ring' },
        { t: 172, enemy: 'n8_hauler', count: 3, formation: 'line' },
        { t: 205, enemy: 'n8_airship', count: 3, formation: 'edge' },
        { t: 232, enemy: 'n8_overseer', count: 2, formation: 'line' },
      ],
    },
    {
      name: 'The Engine Hall',
      duration: 600,
      density: { start: 1.0, end: 1.0 },
      pool: [
        { enemy: 'n8_scuttler', weight: 6 },
        { enemy: 'n8_flak', weight: 3 },
      ],
      waves: [{ t: 2, enemy: 'n8_scuttler', count: 10, formation: 'ring' }],
      boss: { id: 'n8_automaton', at: 20 },
    },
  ],
  loot: [
    { itemId: 'a_steam_harness', weight: 2 },
    { itemId: 'r_clockwork_heart', weight: 2 },
    { itemId: 'w_flintlock', weight: 2 },
    { itemId: 'w_crossbow', weight: 1 },
    { itemId: 'a_chitin', weight: 2 },
    { itemId: 'a_hide', weight: 1 },
    { itemId: 'r_amber', weight: 1 },
    { itemId: 'r_trilobite_eye', weight: 1 },
  ],
  audio: { music: 'music/era8_industrial', ambient: 'amb/factory', sfxSet: 'clockwork' },
};
